import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Field, inputCls } from "@/components/common/Field";
import ProductPicker from "@/components/common/ProductPicker";
import { PrimaryBtn } from "@/components/common/ui";
import { api, errMsg } from "@/lib/api";
import { CATEGORY_ORDER } from "@/lib/format";
import { cn } from "@/lib/utils";

const blank = { code: "", name: "", category: "Roti", stock_unit: "pcs", is_bundle: false, bundle_components: [], purchase_pack_size: 1, sales_pack_size: 1, frozen_dough_of: null, min_stock: 0, active: true, create_fd: true };

export default function ProductDialog({ open, product, products, onClose, onSaved }) {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(product ? { ...blank, ...product, create_fd: false } : blank); }, [open, product]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const num = (k) => (e) => set(k, Number(e.target.value));
  const comps = (products || []).filter((p) => !p.is_bundle && p.category !== "Frozen Dough" && p.id !== f.id);
  const save = async () => {
    setSaving(true);
    try {
      const { id, stock, sort, ...body } = f;
      if (id) await api.put(`/products/${id}`, body);
      else await api.post("/products", body);
      toast.success("Produk tersimpan");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{f.id ? `Ubah ${f.name}` : "Tambah Produk"}</DialogTitle>
          <DialogDescription>Tanpa harga — hanya satuan stok, ukuran pack, dan minimum stok.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kode" required><input value={f.code} onChange={(e) => set("code", e.target.value)} className={inputCls} data-testid="product-code" /></Field>
          <Field label="Kategori" required>
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="product-category">{CATEGORY_ORDER.map((c) => <option key={c}>{c}</option>)}</select>
          </Field>
          <Field label="Nama" required className="sm:col-span-2"><input value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="product-name" /></Field>
          <Field label="Satuan stok"><select value={f.stock_unit} onChange={(e) => set("stock_unit", e.target.value)} className={inputCls} data-testid="product-unit"><option>pcs</option><option>lembar</option></select></Field>
          <Field label="Minimum stok" hint="Di bawah ini muncul garis merah di Rak Roti"><input type="number" min="0" value={f.min_stock} onChange={num("min_stock")} className={inputCls} data-testid="product-min" /></Field>
          <Field label="Isi pack beli (supplier)" hint="Tortilla: 10"><input type="number" min="1" value={f.purchase_pack_size} onChange={num("purchase_pack_size")} className={inputCls} data-testid="product-purchase-pack" /></Field>
          <Field label="Isi pack jual" hint="Tortilla: 8"><input type="number" min="1" value={f.sales_pack_size} onChange={num("sales_pack_size")} className={inputCls} data-testid="product-sales-pack" /></Field>
          {f.category === "Frozen Dough" && (
            <Field label="Frozen Dough dari produk" className="sm:col-span-2">
              <ProductPicker products={products} value={f.frozen_dough_of} onChange={(v) => set("frozen_dough_of", v)} filter={(p) => ["Roti", "Bagel"].includes(p.category) && !p.is_bundle} testid="product-fd-of" />
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active" /> Aktif</label>
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={f.is_bundle} onCheckedChange={(v) => set("is_bundle", v)} data-testid="product-bundle" /> Bundle (bukan item stok)</label>
          {!f.id && ["Roti", "Bagel"].includes(f.category) && !f.is_bundle && (
            <label className="flex items-center gap-2 text-sm font-medium sm:col-span-2"><Switch checked={f.create_fd} onCheckedChange={(v) => set("create_fd", v)} data-testid="product-create-fd" /> Buat item Frozen Dough otomatis</label>
          )}
          {f.is_bundle && (
            <Field label="Komponen bundle (1 bundle = 1 tiap komponen)" className="sm:col-span-2">
              <div className="flex flex-wrap gap-1.5" data-testid="product-bundle-components">
                {comps.map((p) => {
                  const on = f.bundle_components.includes(p.id);
                  return <button key={p.id} type="button" onClick={() => set("bundle_components", on ? f.bundle_components.filter((x) => x !== p.id) : [...f.bundle_components, p.id])}
                    className={cn("press rounded-full border px-3 py-1.5 text-xs font-semibold", on ? "border-ink bg-ink text-white" : "border-zinc-300")}>{p.name}</button>;
                })}
              </div>
            </Field>
          )}
          <PrimaryBtn className="sm:col-span-2" onClick={save} disabled={saving || !f.code || !f.name} data-testid="product-save">{saving ? "Menyimpan…" : "Simpan Produk"}</PrimaryBtn>
        </div>
      </DialogContent>
    </Dialog>
  );
}
